import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

interface ActiveTask {
  id: string;
  label: string;
  type: 'send' | 'intake' | 'eva';
}

const ACTION_LABELS: Record<string, string> = {
  first_contact:         'Semmy verstuurt eerste bericht',
  follow_up:             'Semmy verstuurt follow-up',
  complaint_response:    'Semmy beantwoordt klacht',
  quote_communication:   'Semmy verstuurt offerte',
  scheduling:            'Semmy plant afspraak',
  general_communication: 'Semmy stuurt bericht',
};

function ThinkingDots() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginLeft: 3 }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            display: 'inline-block', width: 3, height: 3, borderRadius: '50%',
            background: 'var(--accent)', flexShrink: 0,
            animation: 'dot-breathe 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.22}s`,
          }}
        />
      ))}
    </span>
  );
}

interface SemmyActivityBarProps {
  partnerId?: string | null;
  bottomOffset?: number;
  dimmed?: boolean;
}

export function SemmyActivityBar({ partnerId, bottomOffset = 80, dimmed = false }: SemmyActivityBarProps) {
  const [activeTasks, setActiveTasks] = useState<ActiveTask[]>([]);
  const [recentDone, setRecentDone] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const doneTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);

  const subscribe = useCallback(() => {
    if (!partnerId || !isSupabaseConfigured) return;

    channelRef.current = supabase
      .channel(`semmy-activity-${partnerId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'semmy_actions',
          filter: `partner_id=eq.${partnerId}`,
        },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          if (!row.autonomous_send) return;

          if (row.status === 'queued') {
            const label = ACTION_LABELS[row.action_type as string] ?? 'Semmy is actief';
            setActiveTasks(prev => {
              if (prev.find(t => t.id === row.id)) return prev;
              return [...prev, { id: row.id as string, label, type: 'send' }];
            });
          } else if (row.status === 'sent' || row.status === 'approved') {
            setActiveTasks(prev => prev.filter(t => t.id !== row.id));
            setRecentDone('Verstuurd');
            if (doneTimer.current) clearTimeout(doneTimer.current);
            doneTimer.current = setTimeout(() => setRecentDone(null), 2500);
          }
        },
      )
      .subscribe();
  }, [partnerId]);

  useEffect(() => {
    subscribe();
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
      if (doneTimer.current) clearTimeout(doneTimer.current);
    };
  }, [subscribe]);

  const isVisible = activeTasks.length > 0 || recentDone !== null;
  if (!isVisible) return null;

  const currentTask = activeTasks[0];
  const label = recentDone ?? currentTask?.label ?? '';

  return (
    <div
      style={{
        position: 'fixed',
        bottom: bottomOffset,
        right: 16,
        zIndex: 9000,
        opacity: dimmed ? 0 : 1,
        pointerEvents: dimmed ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 14px',
        borderRadius: 9999,
        background: 'var(--bg-sidebar)',
        border: '1px solid var(--border-separator)',
        backdropFilter: 'blur(20px)',
        fontSize: 12,
        fontWeight: 500,
        color: 'var(--text-secondary)',
        animation: 'fadeUp 0.2s cubic-bezier(0.16,1,0.3,1) both',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      }}
    >
      <div style={{
        width: 6, height: 6, borderRadius: '50%',
        background: recentDone ? 'var(--success-bright)' : 'var(--accent)',
        flexShrink: 0,
        animation: recentDone ? 'none' : 'dot-breathe 1.5s ease-in-out infinite',
      }} />
      <span>{label}</span>
      {!recentDone && <ThinkingDots />}
    </div>
  );
}
